<script setup>
import FullCalendar from '@fullcalendar/vue3'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import { useDate } from '@/Composables/useDate'
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import { useConfirm } from '@/Composables/useConfirm'
import Modal from '@/Shared/Modal.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import TextInput from '@/Shared/TextInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import Breadcrumb from '@/Shared/Breadcrumb.vue'
import { useInitials } from '@/Composables/useInitials'
import ToggleInput from '@/Shared/ToggleInput.vue'

const props = defineProps({
  showAllAvailabilities: {
    type: Boolean,
    default: false
  },
  phase: {
    type: Object,
    required: true
  },
  availabilities: {
    type: Array,
    required: true
  },
  participants: {
    type: Array,
    required: true
  },
  games: {
    type: Array,
    required: true
  }
})

const events = ref([])
const showModal = ref(false)
const activeTooltip = ref(null)

const isMobile = ref(window.innerWidth <= 768)
const fullCalendarRef = ref(null)
const calendarApi = ref(null)
const currentView = ref(isMobile.value ? 'listWeek' : 'dayGridMonth')

const form = useForm({
  participant_one_id: null,
  participant_two_id: null,
  date_start: null,
  date_end: null,
  observations: null,
  ranking_group_id: null
})

const updateDeviceType = () => {
  const wasMobile = isMobile.value
  isMobile.value = window.innerWidth <= 768

  if (calendarApi.value) {
    if (isMobile.value && !wasMobile) {
      if (!getAvailableViews.value.includes(currentView.value)) {
        changeView('listWeek')
      }
    } else if (isMobile.value && !getAvailableViews.value.includes(currentView.value)) {
      changeView('listWeek')
    }
  }
}

const goToToday = () => {
  if (calendarApi.value) {
    calendarApi.value.today()
    updateCurrentDate()
  }
}

const navigatePrev = () => {
  if (calendarApi.value) {
    calendarApi.value.prev()
    updateCurrentDate()
  }
}

const navigateNext = () => {
  if (calendarApi.value) {
    calendarApi.value.next()
    updateCurrentDate()
  }
}

const changeView = (viewName) => {
  if (calendarApi.value) {
    if (getAvailableViews.value.includes(viewName)) {
      calendarApi.value.changeView(viewName)
      currentView.value = viewName
      updateCurrentDate()
    } else {
      const defaultView = getAvailableViews.value[0]
      calendarApi.value.changeView(defaultView)
      currentView.value = defaultView
      updateCurrentDate()
    }
  }
}

// Variable reactiva para almacenar la fecha actual del calendario
const currentDate = ref(null)

const updateCurrentDate = () => {
  if (calendarApi.value) {
    // Actualizar la fecha reactiva con la fecha actual del calendario
    currentDate.value = calendarApi.value.getDate()
  }
}

const handleDateClick = (info) => {
  let clickedDateStr = info.dateStr

  if (currentView.value === 'dayGridMonth') {
    clickedDateStr += 'T18:00:00'
  }

  const dateStart = useDate(clickedDateStr).format('YYYY-MM-DDTHH:mm')
  const dateEnd = useDate(clickedDateStr).add(1.5, 'hour').format('YYYY-MM-DDTHH:mm')

  form.date_start = dateStart
  form.date_end = dateEnd
  showModal.value = true
}

// Función para abrir el modal de creación de partido con la fecha actual
const openCreateGameModal = () => {
  const now = new Date()
  const roundedMinutes = Math.ceil(now.getMinutes() / 30) * 30
  const dateStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    roundedMinutes
  )
  const dateEnd = new Date(dateStart)
  dateEnd.setMinutes(dateEnd.getMinutes() + 90) // 1.5 horas después

  form.date_start = useDate(dateStart).format('YYYY-MM-DDTHH:mm')
  form.date_end = useDate(dateEnd).format('YYYY-MM-DDTHH:mm')
  showModal.value = true
}

const formatCurrentDate = () => {
  if (!calendarApi.value || !currentDate.value) return ''

  const date = currentDate.value
  const options = { year: 'numeric', month: 'long' }

  if (currentView.value === 'dayGridMonth') {
    return date.toLocaleDateString('es-ES', options)
  } else if (currentView.value === 'timeGridWeek' || currentView.value === 'listWeek') {
    const endDate = new Date(date)
    endDate.setDate(date.getDate() + 6)

    if (date.getMonth() === endDate.getMonth()) {
      return `${date.getDate()} - ${endDate.getDate()} de ${date.toLocaleDateString('es-ES', {
        month: 'long'
      })} de ${date.getFullYear()}`
    } else {
      return `${date.getDate()} ${date.toLocaleDateString('es-ES', {
        month: 'short'
      })} - ${endDate.getDate()} ${endDate.toLocaleDateString('es-ES', {
        month: 'short'
      })} ${date.getFullYear()}`
    }
  } else if (currentView.value === 'timeGridDay') {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  return date.toLocaleDateString('es-ES', options)
}

onUnmounted(() => {
  window.removeEventListener('resize', updateDeviceType)
})

const getAvailableViews = computed(() => {
  return isMobile.value
    ? ['dayGridMonth', 'listWeek', 'timeGridDay']
    : ['dayGridMonth', 'timeGridWeek', 'timeGridDay', 'listWeek']
})

const getInitialView = computed(() => {
  if (isMobile.value) {
    return 'listWeek'
  }
  return 'dayGridMonth'
})

const calendarOptions = {
  validRange: {
    start: useDate(props.phase.date_start).format('YYYY-MM-DD'),
    end: useDate(props.phase.date_end).addDay().format('YYYY-MM-DD')
  },
  height: 'auto',
  aspectRatio: 2.2,
  locale: esLocale,
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
  initialView: getInitialView.value,
  allDaySlot: false,
  nowIndicator: true,
  slotMinTime: '08:00:00',
  slotMaxTime: '24:00:00',
  slotDuration: '00:30:00',
  slotLabelInterval: '01:00:00',
  headerToolbar: false,
  selectable: false,
  longPressDelay: 1000,
  eventLongPressDelay: 1000,
  selectLongPressDelay: 1000,
  buttonText: {
    today: 'Hoy',
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    list: 'Lista'
  },
  dayMaxEvents: true,
  eventTimeFormat: {
    hour: '2-digit',
    minute: '2-digit',
    meridiem: false,
    hour12: false
  },
  slotLabelFormat: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  },
  dateClick: handleDateClick,
  events: events.value,
  eventDidMount: function (info) {
    const isGame = info.event.id && info.event.id.startsWith('game_')
    const gameId = isGame ? info.event.id.split('_')[1] : null

    info.el.addEventListener('click', function (e) {
      e.preventDefault()
      e.stopPropagation()

      if (activeTooltip.value && activeTooltip.value.parentNode) {
        try {
          document.body.removeChild(activeTooltip.value)
        } catch (e) {
          console.error('Error al eliminar tooltip:', e)
        }
        activeTooltip.value = null
      }

      const tooltip = document.createElement('div')
      tooltip.className = 'interactive-tooltip'
      tooltip.style.zIndex = '1000'

      const eventColor = info.event.backgroundColor || '#4f46e5'
      const startTime = info.event.start
        ? new Date(info.event.start).toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit'
          })
        : ''
      const endTime = info.event.end
        ? new Date(info.event.end).toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit'
          })
        : ''
      const groupName = info.event.extendedProps.groupName || ''
      const isNotified = info.event.extendedProps.notified || false

      tooltip.innerHTML = `
          <div class="p-3 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center space-x-3">
                <div class="flex items-center">
                  <div class="w-3 h-3 rounded-full" style="background-color: ${eventColor}"></div>
                </div>
                <h3 class="font-semibold text-gray-900 text-xs sm:text-sm">${info.event.title}</h3>
              </div>
              <button class="close-tooltip text-gray-500 hover:text-gray-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            ${
              startTime
                ? `<div class="text-sm text-gray-700 mb-1"><span class="font-medium">Horario:</span> ${startTime} - ${endTime}</div>`
                : ''
            }
            ${
              groupName
                ? `<div class="text-sm text-gray-700 mb-1"><span class="font-medium">Grupo:</span> ${groupName}</div>`
                : ''
            }
            ${
              isGame
                ? `<div class="text-sm mb-1 ${
                    isNotified ? 'text-green-600' : 'text-amber-600'
                  } font-medium">
                    Estado: ${isNotified ? 'Notificado a participantes' : 'Pendiente de notificar'}
                  </div>`
                : ''
            }

            ${
              isGame
                ? `
              <div class="mt-3 flex justify-end space-x-2">
                ${
                  !isNotified
                    ? `
                  <button class="notify-btn px-2 py-1 bg-primary-500 hover:bg-primary-600 text-white text-xs rounded-md flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Notificar
                  </button>
                `
                    : `
                    <button class="edit-game-btn px-2 py-1 bg-primary-500 hover:bg-primary-600 text-white text-xs rounded-md flex items-center">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"/>
                      </svg>
                      Ver resultado
                    </button>
                  `
                }
                <button class="delete-btn px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-md flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Eliminar
                </button>
              </div>
            `
                : ''
            }
          </div>
        `
      const rect = info.el.getBoundingClientRect()
      tooltip.style.position = 'absolute'
      document.body.appendChild(tooltip)
      const tooltipRect = tooltip.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      let left = rect.left + window.scrollX
      let top = rect.bottom + window.scrollY + 5

      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8
      }
      if (left < 8) {
        left = 8
      }

      if (top + tooltipRect.height > viewportHeight - 8) {
        const above = rect.top + window.scrollY - tooltipRect.height - 5
        if (above > 8) {
          top = above
        } else {
          top = 8
        }
      }

      tooltip.style.left = `${left}px`
      tooltip.style.top = `${top}px`

      activeTooltip.value = tooltip

      function closeTooltip() {
        if (tooltip && tooltip.parentNode) {
          try {
            document.body.removeChild(tooltip)
          } catch (e) {
            console.error('Error al eliminar tooltip:', e)
          }
          activeTooltip.value = null
          document.removeEventListener('mousedown', handleClickOutside)
          window.removeEventListener('scroll', handleScroll, true)
        }
      }
      function handleClickOutside(e) {
        if (!tooltip.contains(e.target)) {
          closeTooltip()
        }
      }
      function handleScroll() {
        closeTooltip()
      }
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)

      tooltip.querySelector('.close-tooltip').addEventListener('click', function () {
        if (tooltip && tooltip.parentNode) {
          try {
            document.body.removeChild(tooltip)
          } catch (e) {
            console.error('Error al eliminar tooltip:', e)
          }
          activeTooltip.value = null
        }
      })

      const deleteBtn = tooltip.querySelector('.delete-btn')
      if (deleteBtn) {
        deleteBtn.addEventListener('click', async function () {
          if (await useConfirm('¿Estás seguro de que quieres eliminar este partido?')) {
            if (tooltip && tooltip.parentNode) {
              try {
                document.body.removeChild(tooltip)
              } catch (e) {
                console.error('Error al eliminar tooltip:', e)
              }
              activeTooltip.value = null
            }

            router.delete(`/rankings/phases/groups/games/${gameId}/delete`, {
              preserveScroll: true,
              preserveState: false,
              onSuccess: () => {
                info.event.remove()
              }
            })
          }
        })
      }

      const notifyBtn = tooltip.querySelector('.notify-btn')
      if (notifyBtn) {
        notifyBtn.addEventListener('click', async function () {
          if (await useConfirm('¿Quieres avisar a los participantes de este partido de ranking?')) {
            if (tooltip && tooltip.parentNode) {
              try {
                document.body.removeChild(tooltip)
              } catch (e) {
                console.error('Error al eliminar tooltip:', e)
              }
              activeTooltip.value = null
            }

            router.post(
              `/rankings/phases/groups/games/${gameId}/notify`,
              {
                preserveScroll: true,
                preserveState: false
              },
              {
                onSuccess: () => {
                  const eventId = `game_${gameId}`
                  const existingEvent = calendarApi.value?.getEventById(eventId)

                  if (existingEvent) {
                    existingEvent.remove()

                    events.value = events.value.map((event) => {
                      if (event.id === eventId) {
                        return {
                          ...event,
                          backgroundColor: '#10b981',
                          borderColor: '#059669',
                          extendedProps: {
                            ...event.extendedProps,
                            notified: true
                          }
                        }
                      }
                      return event
                    })

                    calendarApi.value?.removeAllEvents()
                    calendarApi.value?.addEventSource(events.value)
                  }

                  activeTooltip.value?.remove()
                  activeTooltip.value = null
                }
              }
            )
          }
        })
      }

      const editGameBtn = tooltip.querySelector('.edit-game-btn')
      if (editGameBtn) {
        editGameBtn.addEventListener('click', function () {
          const eventId = `game_${gameId}`
          const event = calendarApi.value?.getEventById(eventId)
          if (event) {
            window.location.href = `/rankings/phases/groups/games/${gameId}/result/edit`
          }
        })
      }
    })
  }
}

const findParticipant = (id) => {
  return props.participants.find((participant) => participant.id === id)
}

const allParticipants = computed(() => {
  return props.participants.map((participant) => ({
    id: participant.id,
    name: participant.participant?.name || 'Sin nombre',
    ranking_group_id: participant.ranking_group_id
  }))
})

const groups = computed(() => {
  return props.phase.groups || []
})

const filteredParticipants = computed(() => {
  if (!form.ranking_group_id) {
    return []
  }
  return allParticipants.value.filter((p) => p.ranking_group_id === form.ranking_group_id)
})

const createGame = () => {
  const dateStart = useDate(form.date_start).format('YYYY-MM-DD HH:mm:ss')
  const dateEnd = useDate(form.date_end).format('YYYY-MM-DD HH:mm:ss')

  form.date_start = dateStart
  form.date_end = dateEnd

  const event = {
    id: `game_${new Date().getTime()}`,
    title: `${findParticipant(form.participant_one_id)?.name} vs ${
      findParticipant(form.participant_two_id)?.name
    }`,
    observations: form.observations,
    start: dateStart,
    end: dateEnd,
    color: '#f3722c',
    textColor: '#ffffff',
    extendedProps: {
      type: 'game'
    }
  }

  form.post(`/rankings/phases/groups/${form.ranking_group_id}/games/store`, {
    preserveScroll: true,
    preserveState: true,
    onSuccess: (component) => {
      event.id = `game_${component.props.flash.info.game.id}`
      events.value.push(event)
      showModal.value = false
      form.ranking_group_id = null
      form.participant_one_id = null
      form.participant_two_id = null
      form.observations = null
    }
  })
}

const showAllAvailabilities = ref(props.showAllAvailabilities)

watch(
  () => showAllAvailabilities.value,
  (s) => {
    router.get(
      `/rankings/phases/${props.phase.id}/calendar`,
      { showAllAvailabilities: s },
      { preserveState: false, preserveScroll: true }
    )
  }
)

onMounted(() => {
  window.addEventListener('resize', updateDeviceType)

  setTimeout(() => {
    if (fullCalendarRef.value) {
      calendarApi.value = fullCalendarRef.value.getApi()
      updateCurrentDate()
      updateDeviceType()
    }
  }, 100)

  props.availabilities.forEach((availability) => {
    const rankingGroupId = props.participants.find(
      (p) => p.id === availability.available_id
    )?.ranking_group_id

    const group = props.phase.groups.find((g) => g.id === rankingGroupId)

    const groupName = group ? group.name : 'Grupo sin nombre'
    const minimalGroupName = useInitials().getInitials(groupName)

    const participantName = props.participants.find((p) => p.id === availability.available_id)
      ?.participant.name

    const participant = props.participants.find((p) => p.id === availability.available_id)

    events.value.push({
      id: `availability_${availability.id}`,
      title: `(${minimalGroupName}) ${participantName}`,
      start: availability.date_start,
      end: availability.date_end,
      backgroundColor: '#4f46e5', // Azul para disponibilidades
      borderColor: '#4338ca',
      extendedProps: {
        type: 'availability',
        groupName: groupName,
        participantId: availability.available_id,
        participantName: participantName,
        participantEmail: participant?.participant?.email || '',
        participantPhone: participant?.participant?.phone || ''
      }
    })
  })

  props.games.forEach((game) => {
    const group = props.phase.groups.find((g) => g.id === game.ranking_group_id)

    const groupName = group?.name

    const minimalGroupName = useInitials().getInitials(groupName)

    const participantOne = findParticipant(game.participant_one_id)
    const participantTwo = findParticipant(game.participant_two_id)

    const isNotified = !!game.notified_at
    const backgroundColor = isNotified ? '#10b981' : '#f3722c'
    const borderColor = isNotified ? '#059669' : '#d97706'

    events.value.push({
      id: `game_${game.id}`,
      title: `(${minimalGroupName}) ${participantOne?.name} vs ${participantTwo?.name}`,
      start: game.date_start,
      end: game.date_end,
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      extendedProps: {
        type: 'game',
        groupName: groupName,
        observations: game.observations,
        notified: isNotified
      }
    })
  })
})
</script>

<template>
  <div>
    <Breadcrumb
      :breadcrumbs="[
        { label: 'Rankings' },
        {
          label: phase.name,
          href: `/rankings/phases/${phase.id}/edit`
        },
        { label: 'Calendario' }
      ]"
    />
    <div
      class="bg-gradient-to-b from-white to-indigo-50/30 rounded-lg shadow-sm overflow-hidden h-full border-0 mt-6"
    >
      <div
        class="border-b border-indigo-100 bg-white p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shadow-sm"
      >
        <div class="flex items-center w-full md:w-auto">
          <h1 class="text-sm md:text-xl font-medium text-secondary-900 w-1/2 md:w-auto">
            {{ phase.name }}
          </h1>

          <div class="ml-4 flex w-1/2 md:w-auto">
            <button
              class="text-sm font-medium text-white hover:text-white bg-primary-500 hover:bg-primary-600 border-0 rounded-md px-3 py-1.5 transition-colors duration-200 mr-2 shadow-sm"
              @click="goToToday"
            >
              Hoy
            </button>
            <div class="flex border border-secondary-100 rounded-md overflow-hidden shadow-sm">
              <button
                class="px-2 py-1 bg-white hover:bg-secondary-50 text-secondary-600 hover:text-primary-600 transition-colors duration-200"
                @click="navigatePrev"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                class="px-2 py-1 bg-white hover:bg-secondary-50 text-secondary-600 hover:text-primary-600 transition-colors duration-200"
                @click="navigateNext"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 self-end sm:self-auto">
          <div v-if="calendarApi" class="text-xs sm:text-sm font-medium text-secondary-700">
            {{ formatCurrentDate() }}
          </div>
          <div class="border border-secondary-100 rounded-md overflow-hidden shadow-sm">
            <div class="flex text-sm">
              <button
                :class="[
                  currentView === 'dayGridMonth'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('dayGridMonth')"
              >
                Mes
              </button>
              <button
                :class="[
                  currentView === 'timeGridWeek'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  getAvailableViews.value?.includes('timeGridWeek') ? '' : 'hidden',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('timeGridWeek')"
              >
                Semana
              </button>
              <button
                :class="[
                  currentView === 'timeGridDay'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('timeGridDay')"
              >
                Día
              </button>
              <button
                :class="[
                  currentView === 'listWeek'
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-white text-secondary-600 hover:bg-secondary-50 hover:text-primary-600',
                  'px-3 py-1 font-medium transition-colors duration-200'
                ]"
                @click="changeView('listWeek')"
              >
                Lista
              </button>
            </div>
          </div>
        </div>

        <!-- El botón de crear partido se ha movido a la parte inferior fija de la pantalla para móviles -->
      </div>

      <div class="p-4 h-[calc(100%-72px)]">
        <ToggleInput
          v-model="showAllAvailabilities"
          label="Ver todas las disponibilidades"
          class="mb-4 flex text-xs"
        />

        <FullCalendar
          ref="fullCalendarRef"
          class="h-full calendar-container"
          :options="calendarOptions"
        />
      </div>
    </div>
  </div>

  <Modal v-if="showModal">
    <form class="bg-white p-4 rounded-lg max-w-lg w-full" @submit.prevent="createGame">
      <h2 class="text-lg font-bold mb-4">Crear partido</h2>

      <SelectInput
        v-model="form.ranking_group_id"
        :error="form.errors.ranking_group_id"
        class="pb-8 w-full"
        label="Selecciona un grupo"
      >
        <option v-for="group in groups" :key="group.id" :value="group.id">
          {{ group.name }}
        </option>
      </SelectInput>

      <SelectInput
        v-model="form.participant_one_id"
        :error="form.errors.participant_one_id"
        class="pb-8 w-full"
        label="Elige un participante"
      >
        <option
          v-for="participant in filteredParticipants"
          :key="participant.id"
          :value="participant.id"
        >
          {{ participant.name }}
        </option>
      </SelectInput>

      <SelectInput
        v-model="form.participant_two_id"
        :error="form.errors.participant_two_id"
        class="pb-8 w-full"
        label="Elige un participante"
      >
        <option
          v-for="participant in filteredParticipants"
          :key="participant.id"
          :value="participant.id"
        >
          {{ participant.name }}
        </option>
      </SelectInput>

      <TextInput
        v-model="form.date_start"
        :error="form.errors.date_start"
        class="pb-8 w-full"
        label="Fecha inicio"
        type="datetime-local"
      />

      <TextInput
        v-model="form.date_end"
        :error="form.errors.date_end"
        class="pb-8 w-full"
        label="Fecha fin"
        type="datetime-local"
      />

      <TextInput
        v-model="form.observations"
        :error="form.errors.observations"
        class="pb-8 w-full"
        label="Pista"
      />

      <div class="grid grid-cols-2 gap-4">
        <LoadingButton type="submit" :loading="form.processing" class="justify-center btn-indigo">
          Confirmar
        </LoadingButton>
        <button type="button" class="btn-white" @click=";(showModal = false), form.reset()">
          Cancelar
        </button>
      </div>
    </form>
  </Modal>

  <div class="fixed bottom-4 right-0 md:right-4 px-4 z-10">
    <button
      type="button"
      class="bg-primary-500 hover:bg-primary-600 text-white w-full flex items-center justify-center py-3 rounded-lg shadow-lg transition-colors duration-200 px-3"
      @click="openCreateGameModal"
    >
      <span class="ml-1 text-xs md:text-sm font-semibold">Crear partido</span>
    </button>
  </div>
</template>

<style scoped>
::v-deep {
  /* Estilos básicos del calendario */
  .fc {
    --fc-border-color: theme('colors.indigo.100');
    --fc-page-bg-color: white;
    --fc-neutral-bg-color: theme('colors.gray.50');
    --fc-event-border-color: theme('colors.indigo.700');
    --fc-event-bg-color: theme('colors.indigo.600');
    --fc-event-text-color: white;
    --fc-event-selected-overlay-color: rgba(0, 0, 0, 0.25);
  }

  /* Estilos para el día actual */
  .fc-day-today {
    background-color: rgba(79, 70, 229, 0.05) !important; /* indigo-600 con transparencia */
  }

  .fc-timegrid-col.fc-day-today {
    background-color: rgba(79, 70, 229, 0.05) !important;
  }

  /* Estilos para los eventos */
  .fc-event {
    border-radius: 4px;
    border-left-width: 4px;
    padding: 4px 6px;
    font-size: 0.8rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: transform 0.1s, box-shadow 0.1s;
    margin-bottom: 1px;
  }

  .fc-event-main {
    padding: 0;
    font-weight: 500;
  }

  .fc-event-time {
    font-weight: 500;
    opacity: 0.8;
    font-size: 0.75rem;
  }

  .fc-event:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Estilos específicos para la vista de mes */
  .fc-daygrid-event {
    border-radius: 4px;
    padding: 2px 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Estilos específicos para la vista de semana/día */
  .fc-timegrid-event-harness {
    margin: 0 1px;
  }

  /* Estilos para los encabezados de columnas */
  .fc-col-header-cell {
    padding: 10px 0;
    font-weight: 600;
    color: #4338ca; /* indigo-700 */
    background-color: rgba(238, 242, 255, 0.5); /* indigo-50 con transparencia */
    border-bottom: 2px solid #e0e7ff; /* indigo-100 */
  }

  /* Estilos para los números de día */
  .fc-daygrid-day-number {
    padding: 8px;
    color: #4b5563; /* gray-600 */
    font-weight: 500;
  }

  /* Estilos para días fuera del mes actual */
  .fc-day-other .fc-daygrid-day-number {
    color: #9ca3af; /* gray-400 */
    opacity: 0.8;
  }

  /* Estilos para el día actual */
  .fc-day-today .fc-daygrid-day-number {
    background-color: #4f46e5; /* indigo-600 */
    color: white;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 4px;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(79, 70, 229, 0.3);
  }

  /* Estilos para la vista de lista */
  .fc-list {
    border: none;
    background-color: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .fc-list-day-cushion {
    background-color: #eef2ff; /* indigo-50 */
    padding: 10px 16px;
    border-left: 4px solid #6366f1; /* indigo-500 */
  }

  .fc-list-event:hover td {
    background-color: #eef2ff; /* indigo-50 */
  }

  /* Estilos para la vista de tiempo */
  .fc-timegrid-slot {
    height: 30px !important; /* Reducido de 48px a 30px */
  }

  /* Estilos para la vista móvil */
  @media (max-width: 640px) {
    .fc-toolbar-title {
      font-size: 1rem !important;
    }

    .fc-daygrid-day-number {
      padding: 4px !important;
      font-size: 0.85rem;
    }

    .fc-col-header-cell {
      padding: 6px 0 !important;
    }

    .fc-timegrid-slot {
      height: 25px !important; /* Reducido de 40px a 25px para móviles */
    }

    .fc-list-day-cushion {
      padding: 8px 12px !important;
    }
  }

  .fc-timegrid-event {
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .fc-timegrid-event:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 10 !important;
  }

  /* Animaciones para cambios de vista */
  .fc-view-harness {
    transition: height 0.3s ease-out;
  }

  /* Mejorar apariencia de celdas de tiempo */
  .fc-timegrid-slot-lane {
    transition: background-color 0.2s;
  }

  .fc-timegrid-slot-lane:hover {
    background-color: rgba(79, 70, 229, 0.03);
  }
}

/* Estilos para el tooltip normal */
:global(.event-tooltip) {
  z-index: 1000;
  position: absolute;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  max-width: 300px;
  pointer-events: none;
  animation: fadeIn 0.2s ease-in-out;
}

/* Estilos para el tooltip interactivo */
:global(.interactive-tooltip) {
  position: absolute;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  max-width: 300px;
  pointer-events: auto;
  animation: fadeIn 0.2s ease-in-out;
}

:global(.interactive-tooltip .close-tooltip) {
  cursor: pointer;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

:global(.interactive-tooltip .close-tooltip:hover) {
  background-color: #f3f4f6;
}

:global(.interactive-tooltip .notify-btn),
:global(.interactive-tooltip .delete-btn) {
  transition: all 0.2s;
  font-weight: 500;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
